import { Component, OnInit, ChangeDetectionStrategy, TemplateRef, ViewChild } from '@angular/core';
import { formatDate } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { UIService } from 'src/app/_services';
import { DialogService } from 'src/app/_services/dialog.service';
import { AuditLog, AuditLogFilters, AuditLogService } from 'src/app/_services/audit-log.service';

/** The filter values with their labels, in the order the selects list them */
const ENTITY_LABELS: Record<string, string> = {
  Partner: 'Partner', Customer: 'Customer', User: 'User', Project: 'Project', Tag: 'Tag', Auth: 'Sign-in',
};
const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  update: 'Update',
  soft_delete: 'Soft delete',
  restore: 'Restore',
  hard_delete: 'Hard delete',
  bulk: 'Bulk',
  'auth.login_success': 'Login succeeded',
  'auth.login_failed': 'Login failed',
  'auth.logout': 'Logout',
  'auth.refresh_rejected': 'Refresh rejected',
  'auth.password_reset_requested': 'Password reset requested',
  'auth.password_reset_skipped': 'Password reset skipped',
  'auth.password_reset_completed': 'Password reset completed',
};
/** Action badge colours; the rest are grey */
const ACTION_BADGES: Record<string, string> = {
  create: 'badge-success',
  restore: 'badge-success',
  update: 'badge-primary',
  soft_delete: 'badge-warning',
  hard_delete: 'badge-danger',
  'auth.login_failed': 'badge-danger',
  'auth.refresh_rejected': 'badge-danger',
};
const NO_FILTERS: AuditLogFilters = { entity: '', action: '', actor: '', record: '', from: '', to: '' };

/** Superadmin page: the audit log, filtered, paged and exported as CSV */
@Component({
    selector: 'app-audit-logs',
    templateUrl: './audit-logs.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AuditLogsComponent implements OnInit {
  @ViewChild('details') details: TemplateRef<unknown>;

  entities = Object.keys(ENTITY_LABELS);
  entityLabels = ENTITY_LABELS;
  actions = Object.keys(ACTION_LABELS);
  actionLabels = ACTION_LABELS;
  actionBadges = ACTION_BADGES;

  /** what the form shows; the list and export use the applied ones */
  filters = { ...NO_FILTERS };
  private applied = { ...NO_FILTERS };
  actors: { email: string, role: string }[] = [];
  logs: AuditLog[] = [];
  count = 0;
  limit = 50;
  offset = 0;
  loading = true;
  msg: string;
  exporting = false;
  /** the entry the details modal shows */
  selected: AuditLog;
  /** the list request in flight, dropped when another page or filter is asked for */
  private request: Subscription;

  constructor(
    private auditLogService: AuditLogService,
    private dialogService: DialogService,
    private modalService: NgbModal,
    private uiservice: UIService,
  ) { }

  ngOnInit() {
    this.uiservice.setTitle('Audit Logs');
    this.getLogs();
    this.auditLogService.actors().subscribe({
      next: ({ actors }) => this.actors = actors,
      // the actor filter still offers All and System
      error: () => undefined,
    });
  }

  apply() {
    this.applied = { ...this.filters };
    this.offset = 0;
    this.getLogs();
  }

  clear() {
    this.filters = { ...NO_FILTERS };
    this.apply();
  }

  /** Go to the previous (-1) or next (1) page, if there is one */
  paginate(step: number) {
    const offset = this.offset + step * this.limit;
    if(offset < 0 || offset >= this.count) { return; }
    this.offset = offset;
    this.getLogs();
  }

  getLogs() {
    this.loading = true;
    this.request?.unsubscribe();
    this.request = this.auditLogService.list(this.applied, this.limit, this.offset).subscribe({
      next: ({ logs, count }) => {
        this.logs = logs;
        this.count = count;
        this.loading = false;
        this.msg = undefined;
      },
      error: ({ error }) => {
        this.logs = [];
        this.count = 0;
        this.loading = false;
        this.msg = 'Could not load the audit log';
        this.showError(error);
      },
    });
  }

  /** The Changes column: the changed fields, a sign-in event's reason, or a bulk write's op */
  changesSummary(log: AuditLog) {
    if(log.entity === 'Auth') { return log.after?.reason; }
    if(log.action === 'bulk') { return log.before?.op; }
    return Object.keys(log.changes || {}).join(', ');
  }

  showDetails(log: AuditLog) {
    this.selected = log;
    this.modalService.open(this.details, { size: 'lg', scrollable: true });
  }

  exportCsv() {
    this.exporting = true;
    this.auditLogService.exportCsv(this.applied).subscribe({
      next: csv => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(csv);
        link.download = `audit-log-${formatDate(new Date(), 'yyyy-MM-dd', 'en-US')}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
        this.exporting = false;
      },
      error: async ({ error }) => {
        // a blob request gets the API's JSON error as a Blob too
        const body = error instanceof Blob ? await error.text().then(JSON.parse).catch(() => undefined) : error;
        this.exporting = false;
        this.showError(body);
      },
    });
  }

  private showError(error: { message?: string | string[] }) {
    this.dialogService.show('Error', [].concat(error?.message || 'Something went wrong!').join(', '));
  }
}
